import Swup from 'swup'
import SwupHeadPlugin from '@swup/head-plugin'
import SwupBodyClassPlugin from '@swup/body-class-plugin'
import SwupPreloadPlugin from '@swup/preload-plugin'
import SwupA11yPlugin from '@swup/a11y-plugin'
import { mountPage, unmountPage } from './lifecycle'
import { emit, getState } from './store'

/**
 * Page transitions without giving up real pages.
 *
 * Every URL is a complete server-rendered WordPress page (own <title>, meta,
 * canonical, hreflang, JSON-LD), linked with plain <a href>. Swup only takes
 * over in the browser: it fetches the next page, swaps #swup and #site-lang,
 * and syncs <head> — so crawlers and AI agents see ordinary pages while
 * visitors keep the sound, the chat and the background running.
 */
const IGNORED_PATHS = /\/wp-(admin|login|json)|\/feed\/?$|sitemap|llms\.txt/
const IGNORED_FILES = /\.(pdf|zip|jpe?g|png|gif|webp|svg|xml|txt|mp3|mp4)$/i

const announcements = {
  '*': { visit: 'Navigiert zu: {title}', url: 'Neue Seite: {url}' },
  'it-IT': { visit: 'Navigato a: {title}', url: 'Nuova pagina: {url}' },
  'it-CH': { visit: 'Navigato a: {title}', url: 'Nuova pagina: {url}' },
  'en-US': { visit: 'Navigated to: {title}', url: 'New page at {url}' },
  'en-GB': { visit: 'Navigated to: {title}', url: 'New page at {url}' },
}

// Everything WP_Admin_Bar renders carries a wp-admin-bar-* id: the nodes (li),
// the groups that hold them (ul) and group containers (div).
const ADMIN_BAR_NODES =
  'li[id^="wp-admin-bar-"], ul[id^="wp-admin-bar-"], .ab-group-container[id^="wp-admin-bar-"]'

// What WP_Admin_Bar renders on a node's link from its href and meta. The
// link's aria-expanded stays: admin-bar.js keeps it in step with the menu.
const ADMIN_BAR_LINK_ATTRIBUTES = [
  'href',
  'onclick',
  'target',
  'title',
  'rel',
  'lang',
  'dir',
  'tabindex',
]

let adminBarTouched = false

// How a node is built: its link, any extra meta HTML and the groups of its
// submenu. A node built differently on the next page is swapped whole.
const nodeShape = (node) =>
  [...node.children]
    .map((part) =>
      part.matches('.ab-sub-wrapper')
        ? [...part.children].map((group) => group.id).join()
        : part.tagName
    )
    .join('|')

function setAdminBarMenuOpen(menu, open) {
  menu.classList.toggle('hover', open)
  const toggle = menu.querySelector('a')
  if (toggle?.hasAttribute('aria-expanded')) toggle.setAttribute('aria-expanded', String(open))
}

// Enter on a menu's own link, or a tap once the bar has been touched, opens or
// closes the menu instead of following the link.
function toggleAdminBarMenu(event) {
  const menu = event.target.closest('.menupop')
  if (!menu || event.target.closest('.ab-sub-wrapper')) return
  event.preventDefault()
  const open = !menu.classList.contains('hover')
  if (open && event.type === 'click') {
    document
      .querySelectorAll('#wpadminbar li.menupop.hover')
      .forEach((other) => setAdminBarMenuOpen(other, false))
  }
  setAdminBarMenuOpen(menu, open)
}

function closeAdminBarMenu(event) {
  const menu = event.key === 'Escape' ? event.target.closest('.menupop') : null
  if (!menu) return
  menu.querySelector('.menupop > .ab-item')?.focus()
  setAdminBarMenuOpen(menu, false)
}

// admin-bar.js wires its menus up once, on load. Nodes a visit brings in get
// the same behaviour here: hover intent, Enter, Escape and tap.
function bindAdminBarMenus(root) {
  const menus = [root, ...root.querySelectorAll('li.menupop')].filter((el) =>
    el.matches('li.menupop')
  )
  menus.forEach((menu) => {
    window
      .hoverintent?.(
        menu,
        () => setAdminBarMenuOpen(menu, true),
        () => setAdminBarMenuOpen(menu, false)
      )
      .options({ timeout: 180 })
    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.ctrlKey && !event.shiftKey) toggleAdminBarMenu(event)
    })
    menu.addEventListener('click', (event) => {
      if (adminBarTouched) toggleAdminBarMenu(event)
    })
  })
  root.querySelectorAll('.ab-item').forEach((item) => {
    item.addEventListener('keydown', closeAdminBarMenu)
  })
}

// Touch devices: from the first touch on the bar a tap opens a menu, and a
// touch outside closes it — admin-bar.js does both, but only for the menus it
// found on load.
function watchAdminBarTouch() {
  const bar = document.getElementById('wpadminbar')
  if (!bar || !('ontouchstart' in window)) return
  const touched = () => {
    adminBarTouched = true
  }
  bar.addEventListener('touchstart', touched, { once: true, passive: true })
  const outside = /Mobile\/.+Safari/.test(navigator.userAgent) ? 'touchstart' : 'click'
  document.body.addEventListener(outside, (event) => {
    if (event.target.closest?.('li.menupop')) return
    bar.querySelectorAll('li.menupop.hover').forEach((menu) => setAdminBarMenuOpen(menu, false))
  })
}

function importAdminBarNode(next) {
  const node = document.importNode(next, true)
  bindAdminBarMenus(node)
  return node
}

// The node is on both pages: update its link and extra HTML in place, keeping
// the elements admin-bar.js and plugins bound their handlers to on load.
function updateAdminBarNode(current, next) {
  if (nodeShape(current) !== nodeShape(next)) {
    current.replaceWith(importAdminBarNode(next))
    return
  }
  const nextParts = [...next.children]
  Array.from(current.children).forEach((part, index) => {
    const nextPart = nextParts[index]
    if (part.matches('.ab-sub-wrapper')) return
    if (part.matches('.ab-item')) {
      syncAdminBarLink(part, nextPart)
    } else if (part.outerHTML !== nextPart.outerHTML) {
      part.replaceWith(document.importNode(nextPart, true))
    }
  })
}

function syncAdminBarLink(link, next) {
  ADMIN_BAR_LINK_ATTRIBUTES.forEach((name) => {
    const value = next.getAttribute(name)
    if (value === null) link.removeAttribute(name)
    else if (link.getAttribute(name) !== value) link.setAttribute(name, value)
  })
  if (link.innerHTML !== next.innerHTML) link.innerHTML = next.innerHTML
}

// The node is new on the next page: insert it where a fresh page load would
// render it, before the first following sibling this page has as well.
function insertAdminBarNode(next) {
  const parent = document.getElementById(next.parentElement?.id ?? '')
  if (!parent) return
  let sibling = next.nextElementSibling
  let before = null
  while (sibling && !before) {
    const match = sibling.id ? document.getElementById(sibling.id) : null
    if (match?.parentElement === parent) before = match
    sibling = sibling.nextElementSibling
  }
  parent.insertBefore(importAdminBarNode(next), before)
}

// Logged-in editors: the admin bar lives outside the containers, so bring it
// in line with the next page's bar. Core and plugins both render nodes that
// depend on the page (edit, customize?url=, delete cache ?path=, duplicate
// ?post=, view counts), so the two bars are diffed node by node rather than
// guessing which nodes matter: nodes on both pages are updated in place, the
// others removed or inserted (a 404 has nothing to edit).
function syncAdminBar(visit) {
  const nextDocument = visit.to.document
  const bar = document.getElementById('wpadminbar')
  const nextBar = nextDocument?.getElementById('wpadminbar')
  if (!bar || !nextBar) return

  // Document order, so a parent is handled before its children.
  const nodes = (root) => [...root.querySelectorAll(ADMIN_BAR_NODES)]
  const currentNodes = new Map(nodes(bar).map((node) => [node.id, node]))
  const ids = new Set([...currentNodes.keys(), ...nodes(nextBar).map((node) => node.id)])
  ids.forEach((id) => {
    const current = currentNodes.get(id)
    const next = nextDocument.getElementById(id)
    if (
      current?.isConnected &&
      next &&
      current.tagName === next.tagName &&
      current.parentElement.id === next.parentElement?.id
    ) {
      if (current.tagName === 'LI') updateAdminBarNode(current, next)
      return
    }
    // Gone, or moved elsewhere on the next page. A parent that was swapped in
    // whole has already brought the next page's copy along.
    current?.remove()
    if (next && !document.getElementById(id)) insertAdminBarNode(next)
  })
}

export function startRouter() {
  const root = document.querySelector('#swup')
  if (root) mountPage(root)

  // Swup needs every container on every page; without them fall back to
  // normal page loads rather than breaking navigation.
  if (!root || !document.querySelector('#site-lang')) return null

  const swup = new Swup({
    containers: ['#swup', '#site-lang'],
    animationSelector: '[class*="transition-"]',
    ignoreVisit: (url, { el } = {}) => {
      if (el?.closest('[data-no-swup]')) return true
      const { pathname } = new URL(url, window.location.href)
      return IGNORED_PATHS.test(pathname) || IGNORED_FILES.test(pathname)
    },
    plugins: [
      new SwupHeadPlugin({ persistAssets: true, awaitAssets: true }),
      new SwupBodyClassPlugin(),
      new SwupPreloadPlugin(),
      new SwupA11yPlugin({ announcements }),
    ],
  })

  // Easy mode means no motion — page changes included.
  swup.hooks.before('visit:start', (visit) => {
    if (getState().easyMode) visit.animation.animate = false
  })

  swup.hooks.before('content:replace', () => unmountPage())
  swup.hooks.on('content:replace', (visit) => {
    const next = document.querySelector('#swup')
    if (next) mountPage(next)
    syncAdminBar(visit)
  })
  swup.hooks.on('page:view', () => emit('page-view', { url: window.location.href }))
  watchAdminBarTouch()

  return swup
}
